import Tabs from "../../../branchDepartment/components/tabs";
import { useSelector, useDispatch } from "react-redux";
import CircularLoader from "../../../../components/loaders/circularLoader";
import { Box, IconButton, Tooltip, Dialog, DialogContent } from "@mui/material";
import SnackbarMessage from "../../../../components/snackBar/snackBar";
import { useState, useEffect } from "react";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import NewLabelOutlinedIcon from "@mui/icons-material/NewLabelOutlined";
import EditTagModal from "./editTagModal";
import { TagCreateForm } from "./tagCreation";
import { getTagsThunk, updateTagThunk } from "../../../contact/contactThunk";
import NameCardList from "../../../branchDepartment/components/nameCardList";
import { qrDialogPaperSx, QrModalHeader } from "../../../qrCode/components/qrUi";

// Small square action button on a tag card
const actionBtnSx = (c) => ({
    width: 28,
    height: 28,
    borderRadius: 1.75,
    color: c,
    bgcolor: "#fff",
    border: `1px solid ${c}40`,
    "&:hover": { color: "#fff", bgcolor: c, borderColor: c },
});

export default function TagsList() {
    const dispatch = useDispatch();
    const { getTags, status, error } = useSelector((state) => state.contacts);
    const [editTagModal, setEditTagModal] = useState(false);
    const [rowData, setRowData] = useState([]);
    const [searchTag, setSearchTag] = useState("");
    const [createOpen, setCreateOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    useEffect(() => {
        dispatch(getTagsThunk());
    }, [dispatch]);

    useEffect(() => {
        if (status === "failed") {
            setSnackbar({
                open: true,
                message: error,
                severity: "error",
            });
        }
    }, [status, error]);

    // ✅ Delete Tag
    const handleDeleteTag = async (item) => {

        const isConfirmed = window.confirm(`Are you sure you want to delete "${item.tag_name}"?`);
        if (!isConfirmed) return;

        try {
            const result = await dispatch(
                updateTagThunk({
                    credentials: {
                        tag_name: item?.tag_name.trim(),
                        tag_id: item?.id,
                        action: "delete",
                    },
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                setSnackbar({
                    open: true,
                    message: result.payload.message,
                    severity: "success",
                });
                dispatch(getTagsThunk({ type: "tags" }));

            } else {
                setSnackbar({
                    open: true,
                    message: result.payload || "Failed to delete tag.",
                    severity: "error",
                });
            }
        } catch {
            setSnackbar({
                open: true,
                message: "Server error. Please try again.",
                severity: "error",
            });
        }
    };

    // Same filter as before
    const filteredTags = status !== "succeeded" ? [] : getTags.filter((item) => item.tag_name.toLowerCase().includes(searchTag.toLowerCase()));

    return (
        <div>
            <Tabs onCreate={() => setCreateOpen(true)}>
                <Box>
                    {/* Loader */}
                    {status === "loading" && (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                            <CircularLoader color="success" />
                        </Box>
                    )}

                    {/* Success */}
                    {status === "succeeded" && (
                        <NameCardList
                            items={filteredTags}
                            getName={(item) => item.tag_name}
                            search={searchTag}
                            onSearchChange={(value) => {
                                // no leading space (same rule as before)
                                if (!value.startsWith(" ")) {
                                    setSearchTag(value)
                                }
                            }}
                            searchPlaceholder="Search Tag"
                            countIcon={<LocalOfferOutlinedIcon />}
                            countLabel="tag"
                            watermark={<LocalOfferOutlinedIcon />}
                            fullText
                            renderActions={(item) => (
                                <>
                                    <Tooltip title="Edit tag" arrow>
                                        <IconButton
                                            onClick={() => {
                                                setEditTagModal(true);
                                                setRowData(item);
                                            }}
                                            sx={actionBtnSx("#2563eb")}
                                        >
                                            <EditOutlinedIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete tag" arrow>
                                        <IconButton onClick={() => handleDeleteTag(item)} sx={actionBtnSx("#dc2626")}>
                                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                        </IconButton>
                                    </Tooltip>
                                </>
                            )}
                        />
                    )}
                </Box>
            </Tabs>

            {/* Create tag popup */}
            <Dialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { ...qrDialogPaperSx, maxWidth: { sm: 460 } } }}
            >
                <QrModalHeader
                    icon={<NewLabelOutlinedIcon />}
                    title="Create Tag"
                    subtitle="Add a new tag for your contacts"
                    onClose={() => setCreateOpen(false)}
                />
                <DialogContent sx={{ p: 0 }}>
                    {/* remounts on each open, so the form starts empty */}
                    {createOpen && (
                        <TagCreateForm
                            embedded
                            onCancel={() => setCreateOpen(false)}
                            onSuccess={(message) => {
                                setCreateOpen(false);
                                setSnackbar({ open: true, message: message || "Tag created", severity: "success" });
                            }}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Snackbar */}
            <SnackbarMessage
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() =>
                    setSnackbar((prev) => ({
                        ...prev,
                        open: false,
                    }))
                }
            />

            {/* Edit Modal */}
            <EditTagModal
                show={editTagModal}
                handleClose={() => { setEditTagModal(false); setRowData([]) }}
                rowData={rowData}
            />
        </div>
    );
}
